import SvgIcon from "@/components/common/SvgIcon";
import StepWrapper from "./StepWraper";
import {
  Button,
  Radio,
  SelectBox,
  TextField,
  Toast,
  Typography,
} from "@/components/kit";
import {
  PeriodsTypeEnums,
  PlansTypeEnums,
  TProduct,
} from "@/features/billing/types";
import { useEffect, useMemo, useState } from "react";
import useGetRetrievePromotionCode from "@/hooks/query/billing/useGetRetrievePromotionCode";
import useGetBillingPlansList from "@/hooks/query/billing/useGetBillingPlansList";
import { PLAN_DETAILS } from "@/features/billing/constants/planDetails";
import { getCurrencySymbol } from "@/features/billing/util/getCurrencySymbol";
import { TCheckoutLinkPayload } from "@/hooks/query/billing/useGetCheckoutLink";
import { useCurrency } from "@/features/billing/util/CurrencyDetector";

function PlanOption({
  option,
  plansList,
  period,
}: {
  option: { label: string; value: string };
  plansList: TProduct[] | undefined;
  period: PeriodsTypeEnums;
}) {
  const product = useMemo(() => {
    return plansList?.find((p: TProduct) => p.id === option.value);
  }, [option.value, plansList]);
  const planDetails = useMemo(() => {
    return PLAN_DETAILS[product?.bundle || PlansTypeEnums.PLATFORM];
  }, [product]);

  const getPrice = (product: TProduct) => {
    const price = product?.prices?.find((p: any) => p.period === period);
    return price?.tiers[0]?.amount || 0;
  };
  return (
    <div className="flex items-center gap-1">
      <SvgIcon
        className="w-5 h-5"
        fill="fill-unset"
        iconName={planDetails.icon}
      />
      <Typography variant="Body1-Regular">
        {option.label}{" "}
        {`(${getCurrencySymbol(product?.prices?.[0]?.currency || "USD")}${getPrice(product as TProduct)})`}
      </Typography>
    </div>
  );
}

function TrioPlanLicense({
  organizationId,
  setCheckoutLinkPayload,
}: {
  organizationId: string;
  setCheckoutLinkPayload: (payload: TCheckoutLinkPayload | null) => void;
}) {
  const [period, setPeriod] = useState<PeriodsTypeEnums>(
    PeriodsTypeEnums.MONTHLY,
  );
  const { currency } = useCurrency();
  const { mutateAsync: getBillingPlansList } = useGetBillingPlansList();
  const [deviceCount, setDeviceCount] = useState<number>(0);
  const [products, setProducts] = useState<TProduct[]>([]);
  const [product, setProduct] = useState<TProduct | null>(null);
  const [promotionCode, setPromotionCode] = useState<string>("");
  const [couponId, setCouponId] = useState<
    | {
        couponId: string;
        percentOff: number;
      }
    | undefined
  >(undefined);
  const {
    refetch,
    isFetching: isRetrieveCode,
    error,
  } = useGetRetrievePromotionCode(promotionCode, organizationId);

  useEffect(() => {
    getBillingPlansList(currency).then((data) => {
      setProducts(data.products);
    });
  }, [currency]);

  useEffect(() => {
    if (!product || deviceCount <= 0) {
      setCheckoutLinkPayload(null);
      return;
    }
    const price = product.prices.find((p) => p.period === period);
    if (!price) return;

    setCheckoutLinkPayload({
      items: [
        {
          price: price.stripePriceId,
          quantity: deviceCount,
        },
      ],
      ...(couponId && { coupon: couponId.couponId }),
    });
  }, [product, period, deviceCount, couponId, setCheckoutLinkPayload]);

  const getPrice = (product: TProduct) => {
    const price = product.prices.find((p: any) => p.period === period);
    return price?.tiers[0]?.amount || 0;
  };

  return (
    <>
      <StepWrapper
        title="💵 Trio plan & license"
        description="Choose the organization plan and the device license for enrollment."
      >
        <div className="flex flex-col gap-8">
          <div className="grid grid-cols-2 gap-8">
            <Typography variant="BodyPlus1-Regular">Billing period</Typography>
            <div className="flex w-full">
              <div className="w-1/2 flex items-center gap-2">
                <Radio
                  id="billing-period-monthly"
                  value="monthly"
                  checked={period === PeriodsTypeEnums.MONTHLY}
                  onChange={() => {
                    setPeriod(PeriodsTypeEnums.MONTHLY);
                  }}
                />
                <Typography variant="BodyPlus2-Regular">Monthly</Typography>
              </div>
              <div className="w-1/2 flex items-center gap-2">
                <Radio
                  id="billing-period-yearly"
                  value="yearly"
                  checked={period === PeriodsTypeEnums.ANNUAL}
                  onChange={() => {
                    setPeriod(PeriodsTypeEnums.ANNUAL);
                  }}
                />
                <Typography variant="BodyPlus2-Regular">Annual</Typography>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Typography variant="BodyPlus1-Regular">Trio plan</Typography>
              <Typography color="text-gray-400" variant="Body3-Regular">
                For managed & unmanaged devices
              </Typography>
            </div>
            <SelectBox
              options={
                products?.map((product: TProduct) => ({
                  label: product.name,
                  value: product.id,
                })) || []
              }
              onChange={(value) =>
                setProduct(
                  products?.find((p: TProduct) => p.id === value) || null,
                )
              }
              renderOption={(option) => (
                <PlanOption
                  option={option}
                  plansList={products}
                  period={period}
                />
              )}
              renderValue={(option) => (
                <PlanOption
                  option={option}
                  plansList={products}
                  period={period}
                />
              )}
              value={product?.id}
            />
            <div className="flex flex-col gap-2">
              <Typography variant="BodyPlus1-Regular">
                Total licenses
              </Typography>
              <Typography color="text-gray-400" variant="Body3-Regular">
                Per device/month · Billed annually
              </Typography>
            </div>
            <TextField
              type="number"
              value={deviceCount}
              onChange={(e) => setDeviceCount(Number(e.target.value))}
            />
            <div className="flex flex-col gap-2">
              <Typography variant="BodyPlus1-Regular">Coupon</Typography>
              <Typography color="text-gray-400" variant="Body3-Regular">
                Enter a valid coupon code
              </Typography>
            </div>
            {!couponId && (
              <div className="flex items-center gap-2">
                <TextField
                  onChange={(e) => setPromotionCode(e.target.value)}
                  value={promotionCode}
                  label="Coupon Code"
                  error={error?.message ? "The coupon code is wrong" : false}
                  endAdornment={
                    couponId && (
                      <SvgIcon iconName="checkFill" fill="fill-green-600" />
                    )
                  }
                />
                <Button
                  onClick={() =>
                    refetch().then((data) => {
                      if (!organizationId) {
                        Toast({
                          description: "Please select an organization first",
                          status: "warning",
                        });
                        return;
                      }
                      if (data && data.data?.couponId)
                        setCouponId({
                          couponId: data.data?.couponId,
                          percentOff: data.data?.percentOff,
                        });
                    })
                  }
                  color="blue"
                  disabled={!promotionCode.length}
                  variant="text"
                  isLoading={isRetrieveCode}
                >
                  Apply
                </Button>
              </div>
            )}
            {couponId && (
              <div className="flex items-center gap-2">
                <TextField
                  value={promotionCode}
                  readOnly
                  endAdornment={
                    couponId && (
                      <SvgIcon
                        onClick={() => {
                          setPromotionCode("");
                          setCouponId(undefined);
                        }}
                        className="cursor-pointer"
                        iconName="closeCircleFill"
                        fill="fill-gray-600"
                      />
                    )
                  }
                />
                <Button color="blue" variant="text" disabled>
                  Applied
                </Button>
              </div>
            )}
          </div>
          {product && (
            <div className="px-5 py-3 flex flex-col gap-4 bg-gray-50 rounded-[8px]">
              <div className="w-full flex flex-col gap-1">
                <div className="flex w-full justify-between items-center">
                  <Typography variant="BodyPlus1-Regular">
                    Billing information
                  </Typography>
                  <SvgIcon fill="fill-unset" iconName="payment-icons" />
                </div>
                <Typography color="text-gray-300" variant="Body3-Regular">
                  You are on an{" "}
                  <strong>
                    {period === PeriodsTypeEnums.MONTHLY
                      ? "monthly"
                      : "annual"}{" "}
                  </strong>
                  billing plan, powered by Stripe.
                </Typography>
              </div>
              <div className="flex flex-col gap-[6px]">
                <div className="flex w-full justify-between">
                  <Typography variant="Body3-Regular">
                    {product?.name}
                  </Typography>
                  <Typography variant="Body3-Regular">
                    {getCurrencySymbol(product?.prices?.[0]?.currency)}
                    {getPrice(product)}
                  </Typography>
                </div>
                <div className="flex items-center gap-1">
                  <Typography color="text-gray-500" variant="Caption1-Regular">
                    {deviceCount} licenses
                  </Typography>
                  <div className="w-1 h-1 bg-gray-200 rounded-full" />
                  <Typography color="text-gray-500" variant="Caption1-Regular">
                    {period === PeriodsTypeEnums.MONTHLY
                      ? "1 month"
                      : "12 months "}
                  </Typography>
                </div>
              </div>
              {couponId && (
                <div className="flex justify-between w-full">
                  <Typography color="text-gray-800" variant="Body3-Regular">
                    Discount ({couponId?.percentOff}%)
                  </Typography>
                  <Typography color="text-gray-500" variant="Caption1-Regular">
                    -{getCurrencySymbol(product?.prices?.[0]?.currency)}
                    {(getPrice(product) * deviceCount * couponId?.percentOff) /
                      100}
                  </Typography>
                </div>
              )}
              <div className="flex justify-between w-full">
                <Typography color="text-gray-800" variant="Body3-Regular">
                  Tax
                </Typography>
                <Typography color="text-gray-500" variant="Caption1-Regular">
                  Calculated at checkout
                </Typography>
              </div>
              <div className="w-full h-px bg-gray-200" />
              <div className="flex justify-between items-end w-full">
                <Typography color="text-gray-800" variant="Body1-Bold">
                  Total (estimated)
                </Typography>
                <div className="flex flex-col gap-2">
                  {couponId && (
                    <Typography
                      color="text-gray-200"
                      variant="Body3-Regular"
                      className="line-through"
                    >
                      {getCurrencySymbol(product?.prices?.[0]?.currency)}
                      {getPrice(product) * deviceCount}
                    </Typography>
                  )}
                  <Typography color="text-gray-800" variant="Body1-Bold">
                    {getCurrencySymbol(product?.prices?.[0]?.currency)}
                    {getPrice(product) * deviceCount -
                      (couponId
                        ? (getPrice(product) *
                            deviceCount *
                            couponId?.percentOff) /
                          100
                        : 0)}
                  </Typography>
                </div>
              </div>
            </div>
          )}
        </div>
      </StepWrapper>
      <div className="flex flex-col gap-1">
        <div className="flex gap-1 items-center">
          <SvgIcon
            fill="fill-green-600"
            iconName="checkCircle"
            className="w-3 h-3"
          />
          <Typography color="text-gray-400" variant="Caption1-Regular">
            Taxes are calculated at checkout and may vary by region.
          </Typography>
        </div>
        <div className="flex gap-1 items-center">
          <SvgIcon
            fill="fill-green-600"
            iconName="checkCircle"
            className="w-3 h-3"
          />
          <Typography color="text-gray-400" variant="Caption1-Regular">
            By proceeding, you agree to the{" "}
            <a
              target="_blank"
              href="https://www.trio.so/terms/"
              rel="noreferrer"
              className="font-bold"
            >
              Terms & Conditions
            </a>
            and{" "}
            <a
              target="_blank"
              href="https://www.trio.so/privacy/"
              rel="noreferrer"
              className="font-bold"
            >
              Privacy Policy{" "}
            </a>
          </Typography>
        </div>
      </div>
    </>
  );
}

export default TrioPlanLicense;
